// ─────────────────────────────────────────────────────────────────────────────
// Inventory analysis tabs — read-only screens over the Accounting V2 inventory
// surface (/api/accounting/inventory/*).
//
// These are the ledger-engine views: a per-product stock card with the running
// quantity and value the server computes, the product's configured valuation
// (FIFO layers / weighted average / standard cost), and the three alert
// families. Nothing here writes; there is no button that mutates state, so the
// server's read permission is the only permission these screens need.
//
// Every tab renders the same four non-happy states, because a silently empty
// table is indistinguishable from a refused request: loading, error,
// permission-denied, and a genuine "nothing matched" empty.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { getApiErrorMessage, getApiErrorStatus } from '@/shared/api/axios';
import { Search } from 'lucide-react';
import { useProductSearch } from '@/features/pos/hooks/usePosApi';
import { useLocations, type SlowMovingResult, type StockAnalysisItem } from '../hooks/useInventoryApi';
import {
  useAccountingLowStockAlerts, useProductValuation, useSlowMovingAnalysis, useStockAging, useStockCard,
  type AccountingLowStockAlert, type ProductValuation, type StockAgingItem, type StockCardLine,
} from '../hooks/useInventoryAnalysisApi';
import {
  formatCount, formatDays, formatMinorAmount, formatReportDate, formatRupees,
} from '../utils/reportFormat';

const TH = 'px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider';
const TD = 'px-3 py-2 text-sm whitespace-nowrap';

function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} className="h-10 w-full" />)}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="text-sm text-muted-foreground text-center py-12">{text}</p>;
}

/**
 * A failed query, stated truthfully. A 403 gets its own wording because
 * "no data" and "you are not allowed the data" are different problems for the
 * person reading the screen, and only one of them is fixed by entering stock.
 */
function QueryProblem({ error, retrySubject }: { error: unknown; retrySubject: string }) {
  const status = getApiErrorStatus(error);
  if (status === 403) {
    return (
      <div className="m-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
        <p className="font-semibold">Permission denied</p>
        <p className="mt-1 text-body-sm">
          The server refused this request (HTTP 403). {retrySubject} needs the accounting
          journal-view permission, which your role has not been granted. No figures are shown.
        </p>
      </div>
    );
  }
  return (
    <div className="m-4 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200">
      {getApiErrorMessage(error, `Could not load ${retrySubject}.`)}
    </div>
  );
}

/** Catalog hit shape returned by /api/catalog/products/search. */
interface CatalogHit {
  _id: string;
  name: string;
  sku?: string;
}

/**
 * The stock card and valuation endpoints both require a productId, so these
 * tabs cannot fetch anything until one is chosen. The search box is the only
 * way to get there — no invented default, no "first row" fallback.
 */
function ProductSearchField({
  value,
  onChange,
  selectedLabel,
}: {
  value: string;
  onChange: (productId: string, label: string) => void;
  selectedLabel: string;
}) {
  const [query, setQuery] = useState('');
  const { data, isLoading, error } = useProductSearch(query);
  const results: CatalogHit[] = Array.isArray(data) ? (data as CatalogHit[]) : [];

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a product by name or SKU…"
          className="pl-9 text-sm"
        />
        {query.trim().length >= 1 && (
          <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-lg border bg-popover text-popover-foreground shadow-lg">
            {isLoading && <p className="px-3.5 py-2.5 text-sm text-muted-foreground">Searching…</p>}
            {error && (
              <p className="px-3.5 py-2.5 text-sm text-rose-600">
                {getApiErrorMessage(error, 'Product search failed.')}
              </p>
            )}
            {!isLoading && !error && results.length === 0 && query.trim().length >= 2 && (
              <p className="px-3.5 py-2.5 text-sm text-muted-foreground">No product matched “{query.trim()}”.</p>
            )}
            {results.map((p) => (
              <button
                key={p._id}
                type="button"
                onClick={() => { onChange(p._id, p.sku ? `${p.name} (${p.sku})` : p.name); setQuery(''); }}
                className="flex w-full items-center justify-between gap-3 border-b px-3.5 py-2.5 text-left text-sm hover:bg-accent last:border-0"
              >
                <span className="font-semibold">{p.name}</span>
                {p.sku && <span className="font-mono text-xs text-muted-foreground">{p.sku}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {value ? `Showing: ${selectedLabel}` : 'No product selected yet — this report needs one product.'}
      </p>
    </div>
  );
}

// ── Stock card ───────────────────────────────────────────────────────────────
// GET /api/accounting/inventory/stock-card?productId=…[&locationId=&startDate=&endDate=]
// The server returns { stockCard[], total, page, limit }; amounts on these lines
// are rupee decimals, not minor units.

export function StockCardTab({
  productId,
  productName,
  onSelectProduct,
}: {
  productId: string;
  productName: string;
  onSelectProduct: (id: string, label: string) => void;
}) {
  const [locationId, setLocationId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { data: locations = [] } = useLocations();

  const { data, isLoading, error } = useStockCard(
    productId ? { productId, locationId: locationId || undefined, startDate: startDate || undefined, endDate: endDate || undefined } : null,
  );
  const lines: StockCardLine[] = data?.stockCard ?? [];

  return (
    <Card className="shadow-xs">
      <CardHeader
        title="Stock Card"
        subtitle="Every movement of one product in order, with the running quantity and value the ledger engine computes."
      />
      <CardBody className="space-y-3">
        <ProductSearchField value={productId} selectedLabel={productName} onChange={onSelectProduct} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <Select
            label="Location"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            options={[{ label: 'All locations', value: '' }, ...locations.map((l) => ({ label: `${l.name} (${l.code})`, value: l.id }))]}
          />
          <Input label="From" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <Input label="To" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          <div className="flex items-end pb-1 text-xs text-muted-foreground">
            {data ? `${lines.length} of ${formatCount(data.total)} movements on this page` : ''}
          </div>
        </div>

        {!productId ? <Empty text="Pick a product to open its stock card." />
          : isLoading ? <Loading />
          : error ? <QueryProblem error={error} retrySubject="the stock card" />
          : lines.length === 0 ? <Empty text="This product has no movements for the selected location and period." />
          : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[1100px] text-left text-sm">
                <thead className="border-b bg-muted/30 text-xs font-semibold uppercase text-muted-foreground">
                  <tr>
                    <th className={TH}>Date</th><th className={TH}>Type</th><th className={TH}>Reference</th>
                    <th className={TH}>Narration</th><th className={`${TH} text-right`}>In</th>
                    <th className={`${TH} text-right`}>Out</th><th className={`${TH} text-right`}>Unit Cost</th>
                    <th className={`${TH} text-right`}>Value</th><th className={`${TH} text-right`}>Running Qty</th>
                    <th className={`${TH} text-right`}>Running Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {lines.map((m) => (
                    <tr key={m._id} className="hover:bg-muted/20">
                      <td className={TD}>{formatReportDate(m.date)}</td>
                      <td className={`${TD} capitalize`}>{(m.movementType || '—').replace(/_/g, ' ')}</td>
                      <td className={`${TD} font-mono text-xs`}>{m.referenceNumber || m.referenceType || '—'}</td>
                      <td className={`${TD} max-w-[260px] truncate`}>{m.narration || '—'}</td>
                      <td className={`${TD} text-right text-emerald-700 dark:text-emerald-400`}>{m.quantityIn ? `+${formatCount(m.quantityIn)}` : '—'}</td>
                      <td className={`${TD} text-right text-rose-700 dark:text-rose-400`}>{m.quantityOut ? `−${formatCount(m.quantityOut)}` : '—'}</td>
                      <td className={`${TD} text-right font-mono`}>{formatRupees(m.unitCost)}</td>
                      <td className={`${TD} text-right font-mono`}>{formatRupees(m.movementValue)}</td>
                      <td className={`${TD} text-right font-semibold`}>{formatCount(m.runningQty)}</td>
                      <td className={`${TD} text-right font-mono`}>{formatRupees(m.runningValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </CardBody>
    </Card>
  );
}

// ── Valuation ────────────────────────────────────────────────────────────────
// GET /api/accounting/inventory/valuation?productId=… — the method is resolved
// from the product's valuationMethod server-side, so the three possible shapes
// are all rendered from whatever fields actually arrived.

function ValuationFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-outline bg-surface px-4 py-3">
      <p className="text-label-caps uppercase text-on-surface-variant">{label}</p>
      <p className="mt-1 font-mono text-data-lg font-semibold text-on-surface">{value}</p>
    </div>
  );
}

export function ValuationTab({
  productId,
  productName,
  onSelectProduct,
}: {
  productId: string;
  productName: string;
  onSelectProduct: (id: string, label: string) => void;
}) {
  const [locationId, setLocationId] = useState('');
  const { data: locations = [] } = useLocations();
  const { data, isLoading, error } = useProductValuation(
    productId ? { productId, locationId: locationId || undefined } : null,
  );
  const valuation: ProductValuation | undefined = data;

  return (
    <Card className="shadow-xs">
      <CardHeader
        title="Stock Valuation"
        subtitle="Closing value on the product's own costing method — FIFO cost layers, weighted average, or standard cost."
      />
      <CardBody className="space-y-3">
        <ProductSearchField value={productId} selectedLabel={productName} onChange={onSelectProduct} />
        <div className="w-64">
          <Select
            label="Location"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            options={[{ label: 'All locations', value: '' }, ...locations.map((l) => ({ label: `${l.name} (${l.code})`, value: l.id }))]}
          />
        </div>

        {!productId ? <Empty text="Pick a product to value its stock." />
          : isLoading ? <Loading rows={2} />
          : error ? <QueryProblem error={error} retrySubject="the valuation" />
          : !valuation ? <Empty text="No valuation returned for this product." />
          : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <ValuationFact label="Costing Method" value={valuation.method ? valuation.method.replace(/_/g, ' ') : '—'} />
                <ValuationFact label="Quantity On Hand" value={formatCount(valuation.totalQty)} />
                <ValuationFact label="Stock Value" value={formatMinorAmount(valuation.totalValueMinor)} />
                {typeof valuation.unitCostMinor === 'number' && <ValuationFact label="Unit Cost" value={formatMinorAmount(valuation.unitCostMinor)} />}
                {typeof valuation.weightedAvgCostMinor === 'number' && <ValuationFact label="Weighted Avg Cost" value={formatMinorAmount(valuation.weightedAvgCostMinor)} />}
                {typeof valuation.varianceFromActualMinor === 'number' && <ValuationFact label="Variance vs Actual" value={formatMinorAmount(valuation.varianceFromActualMinor)} />}
              </div>

              {Array.isArray(valuation.layers) && (
                valuation.layers.length === 0
                  ? <Empty text="No unconsumed FIFO cost layers for this product — nothing to value." />
                  : (
                    <div className="overflow-x-auto rounded-lg border border-border">
                      <table className="w-full text-left text-sm">
                        <thead className="border-b bg-muted/30 text-xs font-semibold uppercase text-muted-foreground">
                          <tr>
                            <th className={TH}>Received</th><th className={TH}>Source</th><th className={TH}>Lot</th>
                            <th className={`${TH} text-right`}>Original Qty</th><th className={`${TH} text-right`}>Remaining</th>
                            <th className={`${TH} text-right`}>Unit Cost</th><th className={`${TH} text-right`}>Layer Value</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {valuation.layers.map((l, i) => (
                            <tr key={`${l.lotNumber || 'layer'}-${i}`} className="hover:bg-muted/20">
                              <td className={TD}>{formatReportDate(l.receivedAt)}</td>
                              <td className={TD}>{l.sourceType || '—'}</td>
                              <td className={`${TD} font-mono text-xs`}>{l.lotNumber || '—'}</td>
                              <td className={`${TD} text-right`}>{formatCount(l.originalQuantity)}</td>
                              <td className={`${TD} text-right font-semibold`}>{formatCount(l.remainingQuantity)}</td>
                              <td className={`${TD} text-right font-mono`}>{formatRupees(l.unitCost)}</td>
                              <td className={`${TD} text-right font-mono`}>{formatRupees(l.layerValue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
              )}
            </div>
          )}
      </CardBody>
    </Card>
  );
}

// ── Low stock (accounting surface) ───────────────────────────────────────────
// GET /api/accounting/inventory/alerts/low-stock — thresholds off safety stock
// / alert quantity, with a severity band. This is deliberately separate from
// the Inventory page's own low-stock tab on /api/inventory/alerts.

const SEVERITY: Record<string, string> = {
  out_of_stock: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
  critical: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

export function LowStockAlertsTab() {
  const { data: locations = [] } = useLocations();
  const [locationId, setLocationId] = useState('');
  const { data, isLoading, error } = useAccountingLowStockAlerts(locationId || undefined);
  const alerts: AccountingLowStockAlert[] = data?.alerts ?? [];

  return (
    <Card className="shadow-xs">
      <CardHeader
        title="Low Stock Alerts"
        subtitle="Products at or below their safety stock / alert threshold, ranked out-of-stock first."
        action={
          <div className="w-56">
            <Select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              options={[{ label: 'All locations', value: '' }, ...locations.map((l) => ({ label: `${l.name} (${l.code})`, value: l.id }))]}
            />
          </div>
        }
      />
      <CardBody className="p-0">
        {isLoading ? <Loading />
          : error ? <QueryProblem error={error} retrySubject="the low-stock alerts" />
          : alerts.length === 0 ? <Empty text="No product is below its threshold. Nothing to reorder." />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b bg-muted/30 text-xs font-semibold uppercase text-muted-foreground">
                  <tr>
                    <th className={TH}>Product</th><th className={TH}>SKU</th>
                    <th className={`${TH} text-right`}>On Hand</th><th className={`${TH} text-right`}>Threshold</th>
                    <th className={`${TH} text-right`}>Shortfall</th><th className={TH}>Severity</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {alerts.map((a) => (
                    <tr key={`${a.productId}-${a.sku ?? ''}`} className="hover:bg-muted/20">
                      <td className={`${TD} font-medium`}>{a.name || '—'}</td>
                      <td className={`${TD} font-mono text-xs text-muted-foreground`}>{a.sku || '—'}</td>
                      <td className={`${TD} text-right font-bold text-rose-700 dark:text-rose-400`}>{formatCount(a.currentStock)}</td>
                      <td className={`${TD} text-right`}>{formatCount(a.threshold)}</td>
                      <td className={`${TD} text-right font-semibold`}>
                        {a.threshold > a.currentStock ? `−${formatCount(a.threshold - a.currentStock)}` : '—'}
                      </td>
                      <td className={TD}>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${SEVERITY[a.severity] ?? SEVERITY.low}`}>
                          {a.severity ? a.severity.replace(/_/g, ' ') : '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </CardBody>
    </Card>
  );
}

// ── Ageing bands ─────────────────────────────────────────────────────────────
// GET /api/accounting/inventory/alerts/aging — age taken from the oldest
// remaining cost layer, falling back to the last movement, then updatedAt.

export function AgingBandsTab() {
  const { data: locations = [] } = useLocations();
  const [locationId, setLocationId] = useState('');
  const { data, isLoading, error } = useStockAging(locationId || undefined);
  const items: StockAgingItem[] = data?.items ?? [];
  const bands = Object.entries(data?.bandSummary ?? {});

  return (
    <Card className="shadow-xs">
      <CardHeader
        title="Stock Ageing"
        subtitle="Age of the stock still on hand, measured from its oldest unconsumed cost layer."
        action={
          <div className="w-56">
            <Select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              options={[{ label: 'All locations', value: '' }, ...locations.map((l) => ({ label: `${l.name} (${l.code})`, value: l.id }))]}
            />
          </div>
        }
      />
      <CardBody className="p-0">
        {isLoading ? <Loading />
          : error ? <QueryProblem error={error} retrySubject="the ageing analysis" />
          : items.length === 0 ? <Empty text="No stock on hand to age." />
          : (
            <div className="space-y-4 p-4">
              <div className="flex flex-wrap gap-2">
                {bands.map(([band, summary]) => (
                  <span key={band} className="rounded-full border border-outline bg-surface-variant px-3 py-1 text-xs text-on-surface-variant">
                    <span className="font-semibold text-on-surface">{band}</span> · {formatCount(summary.itemCount)} items · {formatMinorAmount(summary.valueMinor)}
                  </span>
                ))}
              </div>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-left text-sm">
                  <thead className="border-b bg-muted/30 text-xs font-semibold uppercase text-muted-foreground">
                    <tr>
                      <th className={TH}>Product</th><th className={TH}>SKU</th><th className={TH}>Location</th>
                      <th className={`${TH} text-right`}>On Hand</th><th className={`${TH} text-right`}>Stock Value</th>
                      <th className={`${TH} text-right`}>Age</th><th className={TH}>Band</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {items.map((i) => (
                      <tr key={`${i.productId}-${i.locationId ?? 'all'}`} className="hover:bg-muted/20">
                        <td className={`${TD} font-medium`}>{i.name || '—'}</td>
                        <td className={`${TD} font-mono text-xs text-muted-foreground`}>{i.sku || '—'}</td>
                        <td className={TD}>{locations.find((l) => l.id === (i.locationId ?? ''))?.name || '—'}</td>
                        <td className={`${TD} text-right`}>{formatCount(i.quantityOnHand)}</td>
                        <td className={`${TD} text-right font-mono`}>{formatMinorAmount(i.stockValueMinor)}</td>
                        <td className={`${TD} text-right`}>{formatDays(i.ageDays)}</td>
                        <td className={TD}>{i.ageBand || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </CardBody>
    </Card>
  );
}

// ── Slow moving / non-moving ─────────────────────────────────────────────────
// GET /api/accounting/inventory/alerts/slow-moving?daysPeriod=…&locationId=…
// Same endpoint the Inventory page's Ageing tab reads; here it is narrowable by
// location, which that tab cannot do.

function AnalysisRows({ items }: { items: StockAnalysisItem[] }) {
  if (items.length === 0) return <Empty text="Nothing in this category." />;
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-left text-sm">
        <thead className="border-b bg-muted/30 text-xs font-semibold uppercase text-muted-foreground">
          <tr>
            <th className={TH}>Product</th><th className={TH}>SKU</th>
            <th className={`${TH} text-right`}>On Hand</th><th className={`${TH} text-right`}>Moved in Period</th>
            <th className={`${TH} text-right`}>Stock Value</th><th className={TH}>Last Movement</th><th className={`${TH} text-right`}>Days Idle</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {items.map((i) => (
            <tr key={`${i.productId}-${i.locationId ?? 'all'}`} className="hover:bg-muted/20">
              <td className={`${TD} font-medium`}>{i.name || '—'}</td>
              <td className={`${TD} font-mono text-xs text-muted-foreground`}>{i.sku || '—'}</td>
              <td className={`${TD} text-right`}>{formatCount(i.quantityOnHand)}</td>
              <td className={`${TD} text-right`}>{formatCount(i.totalMovedInPeriod)}</td>
              <td className={`${TD} text-right font-mono`}>{formatMinorAmount(i.stockValueMinor)}</td>
              <td className={TD}>{formatReportDate(i.lastMovementAt)}</td>
              <td className={`${TD} text-right`}>{formatDays(i.daysSinceLastMovement)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SlowMovingTab() {
  const { data: locations = [] } = useLocations();
  const [daysPeriod, setDaysPeriod] = useState(90);
  const [locationId, setLocationId] = useState('');
  const { data, isLoading, error } = useSlowMovingAnalysis(daysPeriod, locationId || undefined);
  const result: SlowMovingResult | undefined = data;

  return (
    <Card className="shadow-xs">
      <CardHeader
        title="Slow & Non-Moving Stock"
        subtitle="Items with little or no outbound movement over the selected window."
        action={
          <div className="flex items-center gap-2">
            <div className="w-40">
              <Select
                value={String(daysPeriod)}
                onChange={(e) => setDaysPeriod(Number(e.target.value))}
                options={[30, 60, 90, 180, 365].map((d) => ({ label: `Last ${d} days`, value: String(d) }))}
              />
            </div>
            <div className="w-48">
              <Select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                options={[{ label: 'All locations', value: '' }, ...locations.map((l) => ({ label: `${l.name} (${l.code})`, value: l.id }))]}
              />
            </div>
          </div>
        }
      />
      <CardBody className="space-y-4">
        {isLoading ? <Loading />
          : error ? <QueryProblem error={error} retrySubject="the slow-moving analysis" />
          : !result ? <Empty text="No analysis returned for this window." />
          : (
            <>
              {typeof result.totalDeadStockValueMinor === 'number' && result.totalDeadStockValueMinor > 0 && (
                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold text-rose-700 dark:text-rose-400">{formatMinorAmount(result.totalDeadStockValueMinor)}</span>{' '}
                  of non-moving stock over the last {daysPeriod} days.
                </p>
              )}
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Non-moving ({formatCount(result.nonMoving?.length ?? 0)})
                </h4>
                <AnalysisRows items={result.nonMoving ?? []} />
              </div>
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Slow moving ({formatCount(result.slowMoving?.length ?? 0)})
                </h4>
                <AnalysisRows items={result.slowMoving ?? []} />
              </div>
            </>
          )}
      </CardBody>
    </Card>
  );
}
